"use client";

import React, { useState, useEffect, useRef } from 'react';
import { liveQuery } from 'dexie';
import { db } from '@/lib/localdb/db';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { MessageCircle, Star } from 'lucide-react';
import { useApp } from '@/hooks/use-app';
import { AgentChat } from './agent-chat';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { agentSync, AgentMessage, asContent, cleanContent } from '@/lib/state/agent-sync';
import { useStore } from '@/lib/state/store';
import { GLASS_PANEL, GLASS_HEADER } from '@/lib/ui-classes';
import { playChatTone } from '@/lib/audio/chat-sound-effects';
import { NotificationService } from '@/lib/notification-service';

/**
 * TeamChatButton - Dedicated trigger button for page headers and cockpits.
 * Displays live unread count and bounce animation.
 */
const TeamChatButton: React.FC<{
  className?: string;
  variant?: "ghost" | "default" | "outline" | "secondary";
  size?: "default" | "sm" | "lg" | "icon";
}> = ({ className, variant = "ghost", size = "icon" }) => {
  const { currentUser } = useApp();
  const [unreadCount, setUnreadCount] = useState(0);
  const open = useStore((s) => s.globalChatOpen);
  const setOpen = useStore((s) => s.setGlobalChatOpen);

  useEffect(() => {
    let mounted = true;
    if (!db || !currentUser) return;

    const subscription = liveQuery(async () => {
      const msgs: AgentMessage[] = await agentSync.listMessages(currentUser, undefined);
      // Count actionable drafts or direct messages from others
      return msgs.filter((m) => {
        if (m.sender === currentUser) return false;
        if (m.status === 'draft' && asContent(m.content)?.recipe) return true;
        return false;
      }).length;
    }).subscribe({
      next: (count) => {
        if (mounted) setUnreadCount(count);
      },
      error: (err) => console.warn('[TeamChatButton] liveQuery error:', err),
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [currentUser]);

  return (
    <Button
      aria-label="Open Team Chat"
      onClick={() => setOpen(!open)}
      variant={variant}
      size={size}
      className={`relative rounded-lg hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
        unreadCount > 0 ? 'animate-pulse ring-2 ring-primary/60 shadow-lg' : ''
      } ${className || ''}`}
    >
      <MessageCircle className="h-5 w-5 text-primary" />
      {unreadCount > 0 && (
        <Badge className="absolute -top-1.5 -right-1.5 bg-rose-600 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full animate-bounce">
          {unreadCount}
        </Badge>
      )}
    </Button>
  );
};



/**
 * GlobalTeamChatPanel - The single slide-over panel mounted in root layout.
 * Includes global incoming message watcher that handles Starred Auto-Popup, Screen Pulse, and Audio.
 */
const GlobalTeamChatPanel: React.FC = () => {
  const { currentUser } = useApp();
  const open = useStore((s) => s.globalChatOpen);
  const setOpen = useStore((s) => s.setGlobalChatOpen);
  const activeTab = useStore((s) => s.globalChatTab);
  const setActiveTab = useStore((s) => s.setGlobalChatTab);
  const directTargetUserId = useStore((s) => s.globalChatDirectTargetUserId);
  const setDirectTargetUserId = useStore((s) => s.setGlobalChatDirectTargetUserId);
  const starredChatChannels = useStore((s) => s.starredChatChannels);
  const triggerAttentionPulse = useStore((s) => s.triggerAttentionPulse);

  const initialLoadDone = useRef(false);
  const knownMessageIds = useRef<Set<string>>(new Set());

  // Global incoming message listener for Starred Auto-Popup, Screen Attention Pulse & Tone
  useEffect(() => {
    let mounted = true;
    if (!db || !currentUser) return;

    const subscription = liveQuery(async () => {
      return await agentSync.listMessages(currentUser, undefined);
    }).subscribe({
      next: (msgs) => {
        if (!mounted) return;

        // First run: just seed the known IDs so we don't spam popups on reload
        if (!initialLoadDone.current) {
          msgs.forEach((m) => {
            if (m.id) knownMessageIds.current.add(m.id);
          });
          initialLoadDone.current = true;
          return;
        }

        // Check for new messages
        for (const msg of msgs) {
          if (!msg.id || knownMessageIds.current.has(msg.id)) continue;
          knownMessageIds.current.add(msg.id);

          // Only process incoming messages from other senders
          if (msg.sender && msg.sender !== currentUser) {
            const msgChannel = msg.channel || (msg.recipient === 'team' ? 'team' : msg.recipient === 'hybrid' ? 'hybrid' : 'direct');
            const targetKey = msg.sender === 'agent' ? 'direct:heather' : `direct:${msg.sender}`;
            const channelKey = msgChannel === 'direct' ? targetKey : msgChannel;
            const fullStarKey = `${currentUser}:${channelKey}`;

            const isStarred = Boolean(
              starredChatChannels[channelKey] ||
              starredChatChannels[fullStarKey] ||
              starredChatChannels[msgChannel]
            );

            // Native PWA notification
            NotificationService.notifyMessage(
              msg.sender === 'agent' ? 'Heather' : msg.sender,
              cleanContent(msg.content)
            );

            if (isStarred) {
              // 1. Trigger Attention Screen Pulse
              triggerAttentionPulse(msg.sender === 'agent' ? 'cyan' : 'amber');

              // 2. Play Priority Audio Chime
              playChatTone('alert', 'high');

              // 3. Auto-Popup Chat Session
              setOpen(true);
              setActiveTab(msgChannel);
              if (msgChannel === 'direct') {
                setDirectTargetUserId(msg.sender === 'agent' ? null : msg.sender);
              }
            } else {
              // Non-starred incoming message: gentle audio notification
              playChatTone('message-pop', 'medium');
            }
          }
        }
      },
      error: (err) => console.warn('[GlobalTeamChatWatcher] liveQuery error:', err),
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [currentUser, starredChatChannels, triggerAttentionPulse, setOpen, setActiveTab, setDirectTargetUserId]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className={`z-[9550] w-full sm:max-w-[540px] p-0 gap-0 flex flex-col border-l border-white/10 ${GLASS_PANEL}`}>
        <SheetHeader className={`px-5 py-3 ${GLASS_HEADER} flex flex-row items-center justify-between space-y-0`}>
          <div className="flex items-center gap-2">
            <SheetTitle className="text-white text-base font-bold">Team Comms</SheetTitle>
          </div>
          <div className="flex bg-black/50 border border-white/10 rounded-lg p-1 mr-6">
            <button
              onClick={() => {
                setActiveTab('team');
                setDirectTargetUserId(null);
              }}
              className={`px-3 py-1 text-xs rounded-md font-bold transition-all ${
                activeTab === 'team' ? 'bg-emerald-600 text-white shadow-md' : 'text-white/60 hover:text-white'
              }`}
              title="Crew Only — Heather is not in this channel"
            >
              Team
            </button>
            <button
              onClick={() => {
                setActiveTab('hybrid');
                setDirectTargetUserId(null);
              }}
              className={`px-3 py-1 text-xs rounded-md font-bold transition-all ${
                activeTab === 'hybrid' ? 'bg-indigo-600 text-white shadow-md' : 'text-white/60 hover:text-white'
              }`}
              title="Team + Heather — Heather only answers when spoken to (@heather)"
            >
              Team + Heather
            </button>
            <button
              onClick={() => {
                setActiveTab('direct');
                setDirectTargetUserId(null);
              }}
              className={`px-3 py-1 text-xs rounded-md font-bold transition-all ${
                activeTab === 'direct' ? 'bg-cyan-600 text-white shadow-md' : 'text-white/60 hover:text-white'
              }`}
              title="Heather Direct — Private 1-on-1 chat with Heather"
            >
              {directTargetUserId ? `Direct (${directTargetUserId})` : 'Heather'}
            </button>
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-hidden min-h-0">
          {currentUser ? (
            <AgentChat
              userId={currentUser}
              defaultChannel={activeTab}
              directTargetUserId={activeTab === 'direct' ? (directTargetUserId || undefined) : undefined}
              visualStyle="heather-overlay"
              className="h-full border-0 shadow-none rounded-none"
            />
          ) : (
            <div className="p-4 text-muted-foreground">Select a user to start chatting.</div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/**
 * GlobalTeamChat - Smart wrapper component.
 * - When `showTrigger={false}` (as in RootLayout), renders the slide-over Sheet panel.
 * - When used in pages / headers (default or `showTrigger={true}`), renders the trigger button.
 */
export const GlobalTeamChat: React.FC<{ showTrigger?: boolean }> = ({ showTrigger }) => {
  if (showTrigger === false) {
    return <GlobalTeamChatPanel />;
  }
  return <TeamChatButton />;
};

export default GlobalTeamChat;
